import { Controller } from "@hotwired/stimulus"
import type { TurboBeforeFetchRequestEvent, TurboSubmitStartEvent } from "@hotwired/turbo"
import Confirmation from "../../confirmation"
import SelectionToolbarController from "../selection_toolbar_controller"
import type SearchFormController from "../search_form_controller"

export default class ListController extends Controller {
  static outlets = ["search-form"]
  static targets = ["addSelectedToGroup", "deleteSelectedBtn", "toolbar"]

  declare searchFormOutlet: SearchFormController
  declare hasSearchFormOutlet: boolean
  declare addSelectedToGroupTarget: HTMLDivElement
  declare deleteSelectedBtnTarget: HTMLButtonElement
  declare toolbarTarget: HTMLElement

  handleAddClientSubmit(event: CustomEvent) {
    // Only close modal on successful submission (non-error response)
    if (!event.detail.success) {
      return
    }

    const target = event.target
    if (target instanceof HTMLFormElement) {
      const modalEl = target.closest(".modal")
      const modal = globalThis.bootstrap.Modal.getInstance(modalEl)
      if (modal) {
        modal.hide()
      }
    }
  }

  deleteSelectedBtnTargetConnected(el: HTMLButtonElement) {
    const form = el.closest("form")
    if (!form) {
      return
    }

    form.addEventListener("turbo:before-fetch-request", async (event: TurboBeforeFetchRequestEvent) => {
      event?.preventDefault()

      if (await Confirmation.confirmBulkDelete("clients")) {
        event.detail.resume(null)
      } else {
        const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
        selectionToolbar?.clearSelected()
      }
    })

    form.addEventListener("turbo:submit-start", (event: TurboSubmitStartEvent) => {
      const selectionToolbar = SelectionToolbarController.getInstance(this.toolbarTarget)
      const ids = selectionToolbar?.selectedIds() || []
      ids.forEach(id => {
        event.detail.formSubmission.fetchRequest.body
          .append("ids[]", id)
      })
    })
  }

  getClientName(el: EventTarget | null) {
    if (el instanceof HTMLElement) {
      return el.closest("tr")?.dataset.clientName
    }
  }

  async confirmDelete(event: TurboBeforeFetchRequestEvent) {
    event.preventDefault()

    if (await Confirmation.confirmDelete(this.getClientName(event.target) || "this client")) {
      event.detail.resume(null)
    }
  }

  appendComposeRecipients(event: TurboSubmitStartEvent) {
    const ids = this.getSelectionToolbarController()?.selectedIds() || []
    ids.forEach(id => {
      event.detail.formSubmission.fetchRequest.body.append("client_ids[]", id)
    })
  }

  getSelectionToolbarController(): SelectionToolbarController | null {
    return SelectionToolbarController.getInstance(this.toolbarTarget)
  }

  // Append search params from the search form to other forms (e.g., delete actions)
  appendSearchParams(event: TurboSubmitStartEvent) {
    if (!this.hasSearchFormOutlet) return

    this.searchFormOutlet.searchParams().forEach((value, key) => {
      if (typeof value === "string") {
        event.detail.formSubmission.fetchRequest.body.append(key, value)
      }
    })
  }
}
